/**
 * Replane Runtime Configuration Loader for React Client
 *
 * Mirrors server/utils/replane-config-loader.js — same pattern, same API.
 * Uses window.__RUNTIME_CONFIG__ (injected by runtime-env-cra) for connection
 * details instead of process.env so values can be changed without a rebuild.
 *
 * Exports:
 *   initReplaneClient()          – call once at app startup (non-blocking)
 *   getReplaneConfig(serviceKey, path, def) – synchronous read; returns def until connected
 *   isReplaneAvailable()         – true after successful connect
 *   isReplaneSettled()           – true once the first connect attempt finished (connected,
 *                                  failed, or Replane disabled/unconfigured): values read now
 *                                  are the ones the page should decide on
 *   onReplaneReady(callback)     – subscribe to connection; returns unsubscribe fn
 *   subscribeToReplaneConfig(serviceKey, cb) – live updates; fires cb whenever
 *                                  that service's config changes (SSE push)
 */

import { Replane } from '@replanejs/sdk'

const RUNTIME = window.__RUNTIME_CONFIG__ ?? {}

const REPLANE_ENABLED =
  `${RUNTIME.REACT_APP_REPLANE_ENABLED ?? 'true'}`.toLowerCase() !== 'false'
const REPLANE_BASE_URL = RUNTIME.REACT_APP_REPLANE_BASE_URL || 'http://localhost:8082'
const REPLANE_SDK_KEY  = RUNTIME.REACT_APP_REPLANE_API_TOKEN || ''

// Retry interval when Replane is unreachable (e.g. still starting up)
const RETRY_INTERVAL_MS = 30000

let _client      = null
let _isConnected = false
let _isSettled   = false

/** Listeners notified when Replane connects (so hooks can re-render). */
const _readyListeners = new Set()

function _notifyReady() {
  _readyListeners.forEach((fn) => fn())
}

// First connect attempt is over (either way). Ready listeners re-read, so hooks waiting on
// "settled" re-render; on failure they keep the hardcoded defaults.
function _markSettled() {
  if (_isSettled) return
  _isSettled = true
  _notifyReady()
}

/**
 * Subscribe to the moment Replane becomes available.
 * Returns an unsubscribe function.
 */
export function onReplaneReady(callback) {
  _readyListeners.add(callback)
  return () => _readyListeners.delete(callback)
}

// ── Live config subscriptions (SSE push) ─────────────────────────────────
// Consumer callbacks per service key. One SDK subscription is registered per
// service key against the CURRENT client; on reconnect the SDK handles are
// dead, so they're re-registered against the new client.
const _configSubscribers = new Map() // serviceKey -> Set<callback>
const _sdkUnsubs = new Map()          // serviceKey -> SDK unsubscribe fn

function _notifyConfigChange(serviceKey) {
  const subs = _configSubscribers.get(serviceKey)
  if (!subs) return
  subs.forEach((fn) => {
    try {
      fn()
    } catch (_) {
      // never let a consumer callback break the SSE notification loop
    }
  })
}

// Register one SDK subscription for a service key against the current client.
function _registerSdkSubscription(serviceKey) {
  if (!_client || _sdkUnsubs.has(serviceKey)) return
  try {
    const unsub = _client.subscribe(serviceKey, () => _notifyConfigChange(serviceKey))
    _sdkUnsubs.set(serviceKey, typeof unsub === 'function' ? unsub : () => {})
  } catch (err) {
    console.warn(`[Replane] subscribe("${serviceKey}") failed:`, err?.message)
  }
}

// Re-register every tracked service key against a freshly connected client.
// Called after each (re)connect — the previous client's handles are gone.
function _rewireAllSubscriptions() {
  _sdkUnsubs.clear()
  for (const serviceKey of _configSubscribers.keys()) {
    _registerSdkSubscription(serviceKey)
  }
}

/**
 * Subscribe to live changes for a Replane service key. The callback fires
 * whenever that service's config is pushed over SSE (and after a reconnect).
 * Returns an unsubscribe function. Safe to call before the SDK connects — the
 * SDK subscription is wired up automatically once connected.
 */
export function subscribeToReplaneConfig(serviceKey, callback) {
  let set = _configSubscribers.get(serviceKey)
  if (!set) {
    set = new Set()
    _configSubscribers.set(serviceKey, set)
  }
  set.add(callback)
  if (_isConnected) _registerSdkSubscription(serviceKey)
  return () => {
    const s = _configSubscribers.get(serviceKey)
    if (!s) return
    s.delete(callback)
    if (s.size === 0) {
      _configSubscribers.delete(serviceKey)
      const unsub = _sdkUnsubs.get(serviceKey)
      if (unsub) {
        try {
          unsub()
        } catch (_) {
          // ignore SDK unsubscribe errors
        }
        _sdkUnsubs.delete(serviceKey)
      }
    }
  }
}

async function _attemptConnect() {
  const client = new Replane()
  await client.connect({
    baseUrl:          REPLANE_BASE_URL,
    sdkKey:           REPLANE_SDK_KEY,
    connectTimeoutMs: 30000,
  })
  _client      = client
  _isConnected = true
  _isSettled   = true
  console.log('[Replane] Connected to', REPLANE_BASE_URL)
  // Wire (or re-wire after a reconnect) live subscriptions onto the new client,
  // then notify ready listeners so hooks re-read the fresh snapshot.
  _rewireAllSubscriptions()
  _notifyReady()
}

/**
 * Connect to the Replane service.
 * Call this once in src/index.js — fire-and-forget (no await needed).
 * If the initial connection fails (e.g. Replane still starting), retries
 * every 30 s in the background. Components re-render via onReplaneReady()
 * whenever the connection eventually succeeds.
 */
export async function initReplaneClient() {
  if (!REPLANE_ENABLED) {
    console.log('[Replane] Disabled — using hardcoded defaults')
    _markSettled()
    return false
  }

  if (!REPLANE_SDK_KEY || REPLANE_SDK_KEY === 'not-configured') {
    console.warn('[Replane] REACT_APP_REPLANE_API_TOKEN not set — using hardcoded defaults')
    _markSettled()
    return false
  }

  try {
    await _attemptConnect()
    return true
  } catch (err) {
    console.warn('[Replane] Could not connect, will retry every', RETRY_INTERVAL_MS / 1000, 's — using hardcoded defaults for now:', err.message)
    _markSettled()

    // Background retry loop — stops as soon as a connection succeeds
    const retryId = setInterval(async () => {
      if (_isConnected) { clearInterval(retryId); return }
      try {
        await _attemptConnect()
        clearInterval(retryId)
      } catch (_) {
        // still unreachable — keep retrying silently
      }
    }, RETRY_INTERVAL_MS)

    return false
  }
}

/**
 * Read a config value from Replane with a fallback default.
 * Returns defaultValue synchronously when Replane is not yet connected.
 *
 * @param serviceKey  Top-level Replane key: 'builder' | 'gameplay' | 'frontend' | 'observability'
 * @param path        Dot-separated path within the service JSON, e.g. 'ui.sidebar_width'
 * @param defaultValue Fallback when Replane is unavailable or path not found
 *
 * Priority: Replane service JSON → defaultValue
 */
export function getReplaneConfig(serviceKey, path, defaultValue = undefined) {
  if (_isConnected && _client) {
    try {
      const serviceObj = _client.get(serviceKey, { default: null })
      if (serviceObj !== null && serviceObj !== undefined) {
        const parsed = typeof serviceObj === 'string' ? JSON.parse(serviceObj) : serviceObj
        const value = path.split('.').reduce((obj, key) => obj?.[key], parsed)
        if (value !== undefined && value !== null) return value
      }
    } catch (err) {
      console.warn(`[Replane] Error reading "${serviceKey}.${path}":`, err.message)
    }
  }
  return defaultValue
}

/**
 * Coerce a Replane value to boolean.
 * Handles string 'true'/'false' from the dashboard.
 */
export function asBool(val) {
  if (typeof val === 'string') return val.toLowerCase() === 'true'
  return Boolean(val)
}

/**
 * Coerce a Replane value to number.
 * Returns fallback if the value cannot be parsed.
 */
export function asNumber(val, fallback) {
  const n = typeof val === 'string' ? parseFloat(val) : val
  return typeof n === 'number' && !Number.isNaN(n) ? n : fallback
}

export function isReplaneAvailable() {
  return REPLANE_ENABLED && _isConnected
}

export function isReplaneSettled() {
  return _isSettled
}
